import {
	ConditionalAccessSettingsModel,
	SaveAppConditionalAccessSettingsModel
} from "client/Api/Apps/EditApp/saveApp";
import {
	AlertBar,
	FormItem,
	RadioButtonGroup,
	RadioButtonGroupItem
} from "client/Components";
import { Browsers, createBrowsersValidation } from "client/Features/ConditionalAccess/Browsers";
import { createDevicesValidation, Devices } from "client/Features/ConditionalAccess/Devices";
import {
	createIpAddressesValidation,
	IpAddresses
} from "client/Features/ConditionalAccess/IpAddresses";
import {
	createOperatingSystemsValidation,
	OperatingSystems
} from "client/Features/ConditionalAccess/OperatingSystems";
import { IValueInputAttachable } from "client/Forms/attachable";
import { setClientId } from "client/Misc/clientId";
import { isNil } from "client/Misc/utils";
import { createValidationConfiguration, IValidationConfiguration } from "client/Validation";
import {
	ReactElement,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import styles from "./ConditionalAccess.module.less";
import { AppType } from "client/Api/Apps/common";
import { isAppAvailableAsSpace } from "client/Features/Workspace/appTypeHelper";

export type ConditionalAccessProps = {
	appType: AppType;
	attachable: IValueInputAttachable<SaveAppConditionalAccessSettingsModel>;
};

export const ConditionalAccess = (props: ConditionalAccessProps): ReactElement => {
	const {
		appType,
		attachable
	} = props;

	const [wasAlertDismissed, setWasAlertDismissed] = useState<boolean>(false);

	const isAvailableAsSpace: boolean = isAppAvailableAsSpace(appType);

	return (
		<>
			{!wasAlertDismissed && (
				<div className={styles.alertContainer}>
					<AlertBar
						kind="info"
						onDismiss={() => setWasAlertDismissed(true)}
						isInline
					>
						{localize(
							isAvailableAsSpace
								? "_ConditionalAccessExplanationForTileAndSpace"
								: "_ConditionalAccessExplanationForTileOnly"
						)}
					</AlertBar>
				</div>
			)}

			<h2 className="title">{localize("_Visibility")}</h2>
			<FormItem
				label={localize("_IfAccessIsNotAllowed")}
				isForGroup
			>
				<RadioButtonGroup
					{...attachable
						.for("hideTilesWhenNotAccessible")
						.attachToRadioButtonGroup()
					}
					options={
						isAvailableAsSpace
							? tileAndSpaceVisibilityOptions
							: tileOnlyVisibilityOptions
					}
				/>
			</FormItem>

			<h2 className="title">{localize("_Devices")}</h2>
			<FormItem
				label={localize("_SupportedOn")}
				isForGroup
			>
				<Devices
					attachable={attachable}
				/>
			</FormItem>

			<h2 className="title">{localize("_OperatingSystems")}</h2>
			<FormItem
				label={localize("_SupportedOn")}
				isForGroup
			>
				<OperatingSystems
					attachable={attachable}
				/>
			</FormItem>

			<h2 className="title">{localize("_Browsers")}</h2>
			<FormItem
				label={localize("_SupportedOn")}
				isForGroup
			>
				<Browsers
					attachable={attachable}
				/>
			</FormItem>

			<h2 className="title">{localize("_IPRanges")}</h2>
			<FormItem
				label={localize("_SupportedOn")}
				isForGroup
			>
				<IpAddresses
					attachable={attachable}
				/>
			</FormItem>
		</>
	);
};
ConditionalAccess.displayName = "ConditionalAccess";

const tileOnlyVisibilityOptions: readonly RadioButtonGroupItem<boolean>[] = [
	{
		text: localize("_ShowGreyedOutTileWithNotification"),
		value: false
	},
	{
		text: localize("_HideTile"),
		value: true
	}
];

const tileAndSpaceVisibilityOptions: readonly RadioButtonGroupItem<boolean>[] = [
	{
		text: localize("_ShowGreyedOutTileAndSpaceWithNotification"),
		value: false
	},
	{
		text: localize("_HideTileAndSpace"),
		value: true
	}
];

const validation = createValidationConfiguration<SaveAppConditionalAccessSettingsModel>();

validation.addValidators(
	createDevicesValidation<SaveAppConditionalAccessSettingsModel>(),
	createOperatingSystemsValidation<SaveAppConditionalAccessSettingsModel>(),
	createBrowsersValidation<SaveAppConditionalAccessSettingsModel>(),
	createIpAddressesValidation<SaveAppConditionalAccessSettingsModel>()
);

export function conditionalAccessValidator(): IValidationConfiguration<SaveAppConditionalAccessSettingsModel> {
	return validation.clone();
}

export function initialConditionalAccessState(
	initialModel: ConditionalAccessSettingsModel | null
): SaveAppConditionalAccessSettingsModel {
	let {
		hideTilesWhenNotAccessible = false,
		devices = [],
		operatingSystems = [],
		browsers = [],
		whitelistIpRanges = [],
		blacklistIpRanges = []
	} = initialModel ?? {};

	devices ??= [];
	operatingSystems ??= [];
	browsers ??= [];
	whitelistIpRanges ??= [];
	blacklistIpRanges ??= [];

	return {
		hideTilesWhenNotAccessible,
		restrictDevices: devices.length > 0,
		devices,
		restrictOperatingSystems: operatingSystems.length > 0,
		operatingSystems,
		restrictBrowsers: browsers.length > 0,
		browsers,
		restrictIp: whitelistIpRanges.length > 0
			|| blacklistIpRanges.length > 0,
		whitelistIpRanges: whitelistIpRanges.map((x) => setClientId({ value: x })),
		blacklistIpRanges: blacklistIpRanges.map((x) => setClientId({ value: x })),
	};
}

export function prepareConditionalAccessForSaving(
	model: SaveAppConditionalAccessSettingsModel
): SaveAppConditionalAccessSettingsModel;
export function prepareConditionalAccessForSaving(
	model: SaveAppConditionalAccessSettingsModel | null
): SaveAppConditionalAccessSettingsModel | null;
export function prepareConditionalAccessForSaving(
	model: SaveAppConditionalAccessSettingsModel | null
): SaveAppConditionalAccessSettingsModel | null {
	if (isNil(model)) {
		return null;
	}

	const {
		hideTilesWhenNotAccessible,
		restrictDevices,
		devices,
		restrictOperatingSystems,
		operatingSystems,
		restrictBrowsers,
		browsers,
		restrictIp,
		whitelistIpRanges,
		blacklistIpRanges,
	} = model;

	return {
		hideTilesWhenNotAccessible,
		restrictDevices,
		devices: restrictDevices
			? devices
			: [],
		restrictOperatingSystems,
		operatingSystems: restrictOperatingSystems
			? operatingSystems
			: [],
		restrictBrowsers,
		browsers: restrictBrowsers
			? browsers
			: [],
		restrictIp,
		whitelistIpRanges: restrictIp
			? whitelistIpRanges
			: [],
		blacklistIpRanges: restrictIp
			? blacklistIpRanges
			: []
	};
}
